/**
 * useCrmFields Hook
 *
 * Fetches cached CRM field metadata for QueryBuilder.
 * Used to show dynamic CRM fields from Bitrix24/etc in routing rules.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useRole } from '@/contexts/RoleContext';
import { useToast } from '@/hooks/use-toast';
import { departmentKeys } from './useDepartments';
import type {
  CrmEntityType,
  CachedCrmField,
  CrmFieldsEntityResponse,
  GetCrmFieldsResponse,
} from '@/lib/api/types';

// Query keys for CRM fields
export const crmFieldsKeys = {
  all: (selectedOrgId: string | null) => ['crm-fields', selectedOrgId] as const,
  fields: (selectedOrgId: string | null) =>
    [...crmFieldsKeys.all(selectedOrgId), 'fields'] as const,
  fieldsByEntity: (selectedOrgId: string | null, entityType?: CrmEntityType) =>
    [...crmFieldsKeys.fields(selectedOrgId), entityType] as const,
  entityTypes: (selectedOrgId: string | null) =>
    [...crmFieldsKeys.all(selectedOrgId), 'entity-types'] as const,
  providers: (selectedOrgId: string | null) =>
    [...crmFieldsKeys.all(selectedOrgId), 'providers'] as const,
  entity: (selectedOrgId: string | null, entityType: CrmEntityType, entityId: string) =>
    [...crmFieldsKeys.all(selectedOrgId), 'entity', entityType, entityId] as const,
};

interface UseCrmFieldsOptions {
  entityType?: CrmEntityType;
  enabled?: boolean;
}

/**
 * Hook for fetching cached CRM fields
 * Returns fields for QueryBuilder dropdowns
 */
export function useCrmFields(options: UseCrmFieldsOptions = {}) {
  const { entityType, enabled = true } = options;
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: crmFieldsKeys.fieldsByEntity(selectedOrgId, entityType),
    queryFn: () => apiClient.crmFields.getCrmFields({ entityType }),
    enabled: enabled && !!selectedOrgId,
    staleTime: 5 * 60 * 1000, // 5 minutes - fields don't change often
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching CRM entity types
 */
export function useCrmEntityTypes() {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: crmFieldsKeys.entityTypes(selectedOrgId),
    queryFn: () => apiClient.crmFields.getEntityTypes(),
    enabled: !!selectedOrgId,
    staleTime: 30 * 60 * 1000, // 30 minutes - entity types rarely change
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching CRM providers
 */
export function useCrmProviders() {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: crmFieldsKeys.providers(selectedOrgId),
    queryFn: () => apiClient.crmFields.getProviders(),
    enabled: !!selectedOrgId,
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for fetching specific CRM entity data
 * Used to display current field values
 */
export function useCrmEntity(
  entityType: CrmEntityType,
  entityId: string,
  enabled = true
) {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();

  return useQuery({
    queryKey: crmFieldsKeys.entity(selectedOrgId, entityType, entityId),
    queryFn: () => apiClient.crmFields.getCrmEntity(entityType, entityId),
    enabled: enabled && !!entityType && !!entityId && !!selectedOrgId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook for refreshing CRM fields from external API
 * Admin action - updates the cache
 */
export function useRefreshCrmFields() {
  const queryClient = useQueryClient();
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useRole();
  const { toast } = useToast();
  const { t } = useTranslation('settings');

  return useMutation({
    mutationFn: (entityType: CrmEntityType) =>
      apiClient.crmFields.refreshCrmFields(entityType),
    onSuccess: (data) => {
      const entityResponse: CrmFieldsEntityResponse = {
        entityType: data.data.entityType,
        fields: data.data.fields,
        updatedAt: data.data.updatedAt,
        cacheStatus: 'cached',
        cacheAgeMinutes: 0,
      };

      queryClient.setQueryData<GetCrmFieldsResponse>(
        crmFieldsKeys.fieldsByEntity(selectedOrgId, data.data.entityType),
        { data: entityResponse }
      );
      // Refresh broad/all-fields view and dependent departments list.
      void queryClient.invalidateQueries({
        queryKey: crmFieldsKeys.fieldsByEntity(selectedOrgId, undefined),
      });
      void queryClient.invalidateQueries({
        queryKey: departmentKeys.list(selectedOrgId),
      });

      const deptInfo = data.data.departmentCount
        ? `, ${data.data.departmentCount} ${t('routing.toast.departments', 'departments')}`
        : '';
      toast({
        title: t('routing.toast.crmFieldsRefreshed', 'CRM fields refreshed'),
        description: `${t('routing.toast.updated', 'Updated')} ${data.data.fieldCount} ${t('routing.toast.fieldsFor', 'fields for')} ${data.data.entityType}${deptInfo}`,
      });
    },
    onError: (error) => {
      toast({
        title: t('routing.toast.crmFieldsRefreshFailed', 'Failed to refresh CRM fields'),
        description: error.message,
        variant: 'error',
      });
    },
  });
}

/**
 * Transform cached CRM fields to format expected by mergeCustomCrmFields
 */
export function transformFieldsForQueryBuilder(response: CrmFieldsEntityResponse): Array<{
  fieldKey: string;
  fieldName: string;
  fieldType: string;
  entityType: string;
  possibleValues?: Array<{ value: string; label: string }>;
}> {
  return response.fields.map((field: CachedCrmField) => ({
    fieldKey: field.fieldId,
    fieldName: field.title,
    fieldType: field.type,
    entityType: response.entityType,
    possibleValues: field.enumValues?.map((v) => ({
      value: v.id,
      label: v.value,
    })),
  }));
}
